import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { BadgeCheck, ShieldCheck, Calendar, Share2, ArrowLeft, Loader2, Check, Globe } from "lucide-react";
import { useLang } from "../context/LanguageContext";
import { profSummary } from "../content/professions";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SEAL_T = { sk: "Zapísaný vo verejnom registri SauberEko®", en: "Listed in the public SauberEko® register", de: "Eingetragen im öffentlichen SauberEko®-Register", pl: "Wpisany do publicznego rejestru SauberEko®", fr: "Inscrit au registre public SauberEko®" };

const I18N = {
  sk: {
    overline: "Overený certifikát",
    kind: { professional: "Certifikovaný profesionál podľa štandardu Sa.Eko.Prs©", company: "Certifikovaná firma podľa štandardu Sa.Eko.Fa©", recruiter: "Kvalifikovaný podľa štandardu SauberEko®" },
    certLabel: "Číslo certifikátu",
    since: "Certifikovaný od",
    website: "Webová stránka",
    status: "Platný certifikát",
    verified: "Overený vo verejnom registri SauberEko®",
    share: "Zdieľať vizitku",
    copied: "Odkaz skopírovaný",
    back: "Späť do Siene slávy",
    loading: "Načítavam certifikát…",
    notFound: "Certifikát s týmto číslom sa v registri nenašiel.",
    metaDesc: (n) => `${n} — overený certifikát v registri SauberEko®. Certifikovaný profesionál podľa európskeho štandardu Sa.Eko.Prs©.`,
  },
  en: {
    overline: "Verified certificate",
    kind: { professional: "Certified professional under the Sa.Eko.Prs© standard", company: "Certified company under the Sa.Eko.Fa© standard", recruiter: "Qualified under the SauberEko® standard" },
    certLabel: "Certificate number",
    since: "Certified since",
    website: "Website",
    status: "Valid certificate",
    verified: "Verified in the public SauberEko® register",
    share: "Share card",
    copied: "Link copied",
    back: "Back to the Hall of Fame",
    loading: "Loading certificate…",
    notFound: "No certificate with this number was found in the register.",
    metaDesc: (n) => `${n} — verified certificate in the SauberEko® register. Certified professional under the European Sa.Eko.Prs© standard.`,
  },
  de: {
    overline: "Verifiziertes Zertifikat",
    kind: { professional: "Zertifizierter Fachmann nach dem Sa.Eko.Prs©-Standard", company: "Zertifiziertes Unternehmen nach dem Sa.Eko.Fa©-Standard", recruiter: "Qualifiziert nach dem SauberEko®-Standard" },
    certLabel: "Zertifikatsnummer",
    since: "Zertifiziert seit",
    website: "Website",
    status: "Gültiges Zertifikat",
    verified: "Verifiziert im öffentlichen SauberEko®-Register",
    share: "Karte teilen",
    copied: "Link kopiert",
    back: "Zurück zur Ruhmeshalle",
    loading: "Zertifikat wird geladen…",
    notFound: "Kein Zertifikat mit dieser Nummer im Register gefunden.",
    metaDesc: (n) => `${n} — verifiziertes Zertifikat im SauberEko®-Register. Zertifizierter Fachmann nach dem europäischen Sa.Eko.Prs©-Standard.`,
  },
  pl: {
    overline: "Zweryfikowany certyfikat",
    kind: { professional: "Certyfikowany profesjonalista wg standardu Sa.Eko.Prs©", company: "Certyfikowana firma wg standardu Sa.Eko.Fa©", recruiter: "Wykwalifikowany wg standardu SauberEko®" },
    certLabel: "Numer certyfikatu",
    since: "Certyfikowany od",
    website: "Strona internetowa",
    status: "Ważny certyfikat",
    verified: "Zweryfikowany w publicznym rejestrze SauberEko®",
    share: "Udostępnij wizytówkę",
    copied: "Link skopiowany",
    back: "Powrót do Galerii Sław",
    loading: "Ładowanie certyfikatu…",
    notFound: "Nie znaleziono certyfikatu o tym numerze w rejestrze.",
    metaDesc: (n) => `${n} — zweryfikowany certyfikat w rejestrze SauberEko®. Certyfikowany profesjonalista wg europejskiego standardu Sa.Eko.Prs©.`,
  },
  fr: {
    overline: "Certificat vérifié",
    kind: { professional: "Professionnel certifié selon le standard Sa.Eko.Prs©", company: "Entreprise certifiée selon le standard Sa.Eko.Fa©", recruiter: "Qualifié selon le standard SauberEko®" },
    certLabel: "Numéro de certificat",
    since: "Certifié depuis",
    website: "Site web",
    status: "Certificat valide",
    verified: "Vérifié dans le registre public SauberEko®",
    share: "Partager la carte",
    copied: "Lien copié",
    back: "Retour au Panthéon",
    loading: "Chargement du certificat…",
    notFound: "Aucun certificat portant ce numéro n'a été trouvé dans le registre.",
    metaDesc: (n) => `${n} — certificat vérifié dans le registre SauberEko®. Professionnel certifié selon le standard européen Sa.Eko.Prs©.`,
  },
};

const fmtDate = (iso, lang) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString({ sk: "sk-SK", en: "en-GB", de: "de-DE", pl: "pl-PL", fr: "fr-FR" }[lang] || "sk-SK", { day: "2-digit", month: "long", year: "numeric" });
  } catch { return String(iso).slice(0, 10); }
};

const normUrl = (u) => (/^https?:\/\//i.test(u) ? u : "https://" + u);
const prettyUrl = (u) => u.replace(/^https?:\/\//i, "").replace(/\/$/, "");

const setMeta = (name, attr, content) => {
  let el = document.head.querySelector(`meta[${attr}='${name}']`);
  if (!el) { el = document.createElement("meta"); el.setAttribute(attr, name); document.head.appendChild(el); }
  el.setAttribute("content", content);
};

const Initials = ({ name }) => {
  const letters = (name || "").split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]).join("").toUpperCase();
  return (
    <div className="w-20 h-20 rounded-full flex items-center justify-center bg-gradient-to-b from-[#F2E3C6] to-[#D4AF37] text-black font-display font-semibold text-3xl shadow-[0_10px_40px_rgba(212,175,55,0.4)] ring-1 ring-[#F2E3C6]/50">
      {letters || "•"}
    </div>
  );
};

export default function CertifikatProfile() {
  const { certNo } = useParams();
  const { lang } = useLang();
  const tr = I18N[lang] || I18N.sk;
  const [state, setState] = useState({ loading: true, data: null, error: false });
  const [copied, setCopied] = useState(false);
  const [badge, setBadge] = useState(null);
  const [prof, setProf] = useState(null);

  useEffect(() => {
    const key = String(certNo || "").replace(/\s+/g, "").toLowerCase();
    fetch(`${API}/hof/badges`).then((r) => r.json()).then((d) => { setBadge((d.badges || {})[key] || null); setProf((d.professions || {})[key] || null); }).catch(() => {});
  }, [certNo]);
  const badgeTitle = badge && (badge.i18n?.[lang]?.title || badge.title);
  const badgeText = badge && (badge.i18n?.[lang]?.text || badge.text);

  useEffect(() => {
    window.scrollTo(0, 0);
    let alive = true;
    setState({ loading: true, data: null, error: false });
    fetch(`${API}/hof/profile/${encodeURIComponent(certNo)}`)
      .then((r) => { if (!r.ok) throw new Error("nf"); return r.json(); })
      .then((d) => { if (alive) setState({ loading: false, data: d, error: false }); })
      .catch(() => { if (alive) setState({ loading: false, data: null, error: true }); });
    return () => { alive = false; };
  }, [certNo]);

  useEffect(() => {
    const d = state.data;
    if (!d) { document.title = `SauberEko® — ${certNo}`; return; }
    const title = `${d.name} — ${d.certificate_no} | SauberEko®`;
    document.title = title;
    setMeta("description", "name", tr.metaDesc(d.name));
    setMeta("og:title", "property", title);
    setMeta("og:description", "property", tr.metaDesc(d.name));
    setMeta("og:type", "property", "profile");
    // JSON-LD structured data
    let ld = document.getElementById("hof-ld");
    if (!ld) { ld = document.createElement("script"); ld.id = "hof-ld"; ld.type = "application/ld+json"; document.head.appendChild(ld); }
    ld.textContent = JSON.stringify({
      "@context": "https://schema.org", "@type": "Person", name: d.name,
      hasCredential: { "@type": "EducationalOccupationalCredential", credentialCategory: "certification", identifier: d.certificate_no, recognizedBy: { "@type": "Organization", name: "SauberEko®" } },
    });
    return () => { const n = document.getElementById("hof-ld"); if (n) n.remove(); };
  }, [state.data, certNo, tr]);

  const share = async () => {
    const url = window.location.href;
    try {
      if (navigator.share) { await navigator.share({ title: state.data?.name, url }); return; }
      await navigator.clipboard.writeText(url);
      setCopied(true); setTimeout(() => setCopied(false), 2000);
    } catch { /* ignore */ }
  };

  return (
    <div data-testid="certifikat-profile-page" className="min-h-screen bg-[#070707] pt-28 pb-24 relative overflow-hidden">
      <div className="absolute left-1/2 top-28 -translate-x-1/2 w-[600px] h-[420px] bg-[#D4AF37]/8 blur-[130px] rounded-full pointer-events-none" />
      <div className="relative max-w-xl mx-auto px-6">
        <Link to="/sien-slavy" data-testid="cert-back" className="inline-flex items-center gap-2 text-white/50 hover:text-[#D4AF37] text-sm transition-colors mb-8">
          <ArrowLeft size={16} /> {tr.back}
        </Link>

        {state.loading && (
          <div data-testid="cert-loading" className="flex items-center justify-center gap-3 text-white/50 mt-16">
            <Loader2 size={20} className="animate-spin text-[#D4AF37]" /> {tr.loading}
          </div>
        )}

        {state.error && (
          <p data-testid="cert-notfound" className="text-center text-white/50 mt-16">{tr.notFound}</p>
        )}

        {state.data && (
          <div data-testid="cert-card" className="bg-gradient-to-b from-[#111110] to-[#070707] border border-[#D4AF37]/25 overflow-hidden">
            <div className="h-1 w-full bg-gradient-to-r from-transparent via-[#D4AF37] to-transparent" />
            <div className="px-5 sm:px-10 py-9 sm:py-10">
              <div className="flex items-center justify-center gap-3 text-xs tracking-[0.3em] uppercase text-[#D4AF37] mb-8">
                <span className="h-px w-8 bg-[#D4AF37]/60" />
                {tr.overline}
                <span className="h-px w-8 bg-[#D4AF37]/60" />
              </div>

              <div className="flex flex-col items-center text-center">
                <Initials name={state.data.name} />
                <h1 className="font-display text-3xl sm:text-4xl text-white mt-6">{state.data.name}</h1>
                <p className="text-white/60 text-sm md:text-base mt-3 max-w-sm">{tr.kind[state.data.kind] || tr.kind.professional}</p>
              </div>

              <div className="mt-9 space-y-px bg-white/5 border border-white/10">
                <Row icon={BadgeCheck} label={tr.certLabel} value={state.data.certificate_no} mono />
                <Row icon={Calendar} label={tr.since} value={fmtDate(state.data.certified_since, lang)} />
                {state.data.website && (
                  <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-3 px-4 py-3.5 bg-[#0d0d0c]">
                    <span className="flex items-center gap-2.5 text-white/45 text-sm sm:w-36 shrink-0">
                      <Globe size={17} className="text-[#D4AF37]/70 shrink-0" />
                      {tr.website}
                    </span>
                    <a
                      href={normUrl(state.data.website)}
                      target="_blank"
                      rel="noreferrer"
                      data-testid="cert-website"
                      className="text-[#D4AF37] hover:text-[#F2E3C6] text-sm underline underline-offset-2 break-all pl-[27px] sm:pl-0"
                    >
                      {prettyUrl(state.data.website)}
                    </a>
                  </div>
                )}
              </div>

              <div className="mt-6 flex items-center gap-3 bg-[#D4AF37]/10 border border-[#D4AF37]/25 px-4 py-3">
                <ShieldCheck size={22} className="text-[#D4AF37] shrink-0" />
                <div>
                  <p className="text-[#F2E3C6] text-sm font-semibold" data-testid="cert-status-title">{badgeTitle || tr.status}</p>
                  <p className="text-white/55 text-xs leading-relaxed mt-0.5" data-testid="cert-status-text">{badgeText || tr.verified}</p>
                  {prof && <p className="text-[#F2E3C6] text-xs font-semibold mt-1.5" data-testid="cert-professions">{profSummary(prof, lang)}</p>}
                </div>
              </div>

              <button
                data-testid="cert-share"
                onClick={share}
                className="w-full mt-6 inline-flex items-center justify-center gap-2 bg-[#D4AF37] text-black font-semibold px-6 py-3.5 hover:bg-[#F2E3C6] transition-colors duration-300"
              >
                {copied ? <><Check size={18} /> {tr.copied}</> : <><Share2 size={18} /> {tr.share}</>}
              </button>
              <div className="mt-6 flex items-center justify-center gap-3" data-testid="cert-registered-seal">
                <img src="/saubereko-hall-of-fame-registered.png" alt="SauberEko® Hall of Fame – Registered" className="w-14 h-14 opacity-90" loading="lazy" />
                <span className="text-white/40 text-[11px] tracking-[0.15em] uppercase">{SEAL_T[lang] || SEAL_T.sk}</span>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const Row = ({ icon: Icon, label, value, mono }) => (
  <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-3 px-4 py-3.5 bg-[#0d0d0c]">
    <span className="flex items-center gap-2.5 text-white/45 text-sm sm:w-36 shrink-0">
      <Icon size={17} className="text-[#D4AF37]/70 shrink-0" />
      {label}
    </span>
    <span className={`text-white/90 text-sm pl-[27px] sm:pl-0 break-words ${mono ? "font-mono text-[#F2E3C6] tracking-tight" : ""}`}>{value}</span>
  </div>
);
